import { useRef, useState } from "react";

import { useCreateDataRoomNodeFile } from "@vega-api/generated/coinvest-deal-data-room-files-gp/coinvest-deal-data-room-files-gp";
import type { DataRoomFile } from "@vega-api/generated/types/dataRoomFile";

import type {
  NodeMap,
  TreeNode,
} from "../components/DataRoomPermissions/types";
import {
  buildTreeFromDropEntries,
  type BuiltTreeResult,
  type ConflictResolution,
  type DropEntry,
  type DroppedFile,
  type FileConflict,
  type FileReplacement,
  type MergeTarget,
  readDropEntries,
  type RejectedFile,
  validateFiles,
} from "../components/DataRoomPermissions/utils/dropUtils";

export type FileUploadStatus = "done" | "error" | "pending" | "uploading";

export type UploadFileItem = {
  nodeId: string;
  fileName: string;
  file?: File;
  status: FileUploadStatus;
  error?: string;
  isReplacement?: boolean;
};

export type UploadState = {
  isUploading: boolean;
  hasErrors: boolean;
  items: UploadFileItem[];
  completedCount: number;
  totalCount: number;
  rejectedFiles: RejectedFile[];
};

type UseDataRoomUploadParams = {
  dealId: string;
  dataRoomId: string | undefined;
  getNodeMap: () => NodeMap;
  addNodes: (nodes: TreeNode[], mergeTargets?: MergeTarget[]) => void;
  removeNode: (nodeId: string) => void;
  setNodeFile: (nodeId: string, file: DataRoomFile) => void;
  replaceNodeFile: (nodeId: string, file: DataRoomFile) => void;
};

type DeferredDrop = {
  entries: DropEntry[];
  targetParentId: string | null;
  detected: BuiltTreeResult;
};

type ValidReplacementEntry = {
  existingNodeId: string;
  file: File;
  resolvedName: string;
};

export function useDataRoomUpload({
  dealId,
  dataRoomId,
  getNodeMap,
  addNodes,
  removeNode,
  setNodeFile,
  replaceNodeFile,
}: UseDataRoomUploadParams) {
  const { mutateAsync: uploadFile } = useCreateDataRoomNodeFile();
  const [items, setItems] = useState<UploadFileItem[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<RejectedFile[]>([]);
  const [deferredDrop, setDeferredDrop] = useState<DeferredDrop | null>(null);
  const abortRef = useRef(false);

  const isUploading = items.some(
    (item) => item.status === "uploading" || item.status === "pending",
  );
  const completedCount = items.filter((item) => item.status === "done").length;
  const hasErrors = items.some((item) => item.status === "error");

  const uploadState: UploadState = {
    isUploading,
    hasErrors,
    items,
    completedCount,
    totalCount: items.length,
    rejectedFiles,
  };

  const pendingConflicts: FileConflict[] | null = deferredDrop
    ? deferredDrop.detected.conflicts
    : null;

  async function processFileQueue(fileItems: UploadFileItem[]) {
    for (const item of fileItems) {
      if (abortRef.current) break;

      if (!item.file) continue;

      setItems((prev) =>
        prev.map((i) =>
          i.nodeId === item.nodeId ? { ...i, status: "uploading" } : i,
        ),
      );

      try {
        if (!dataRoomId) throw new Error("Data room not available");

        const response = await uploadFile({
          dealId,
          dataRoomId,
          data: { file: item.file },
        });

        if (item.isReplacement) {
          replaceNodeFile(item.nodeId, response.data.file);
        } else {
          setNodeFile(item.nodeId, response.data.file);
        }

        setItems((prev) =>
          prev.map((i) =>
            i.nodeId === item.nodeId
              ? { ...i, status: "done", file: undefined }
              : i,
          ),
        );
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Upload failed";

        setItems((prev) =>
          prev.map((i) =>
            i.nodeId === item.nodeId
              ? { ...i, status: "error", error: message }
              : i,
          ),
        );
      }
    }
  }

  function validateReplacementEntries(replacements: FileReplacement[]): {
    validReplacements: ValidReplacementEntry[];
    rejected: RejectedFile[];
  } {
    const nodeMap = getNodeMap();
    const replacementFileEntries = replacements.map((replacement) => ({
      nodeId: replacement.existingNodeId,
      resolvedName:
        nodeMap[replacement.existingNodeId]?.name ?? replacement.file.name,
      file: replacement.file,
    }));
    const { valid: validReplacementEntries, rejected } = validateFiles(
      replacementFileEntries,
    );

    return {
      validReplacements: validReplacementEntries.map((entry) => ({
        existingNodeId: entry.nodeId,
        file: entry.file,
        resolvedName: entry.resolvedName,
      })),
      rejected,
    };
  }

  async function enqueueFiles(
    nodes: TreeNode[],
    fileEntries: DroppedFile[],
    mergeTargets: MergeTarget[] = [],
    replacements: FileReplacement[] = [],
  ) {
    const { valid, rejected } = validateFiles(fileEntries);
    const { validReplacements, rejected: replacementRejections } =
      validateReplacementEntries(replacements);

    const rejectedNodeIds = new Set(
      fileEntries
        .filter((fe) => !valid.some((v) => v.nodeId === fe.nodeId))
        .map((fe) => fe.nodeId),
    );

    const validNodes = nodes
      .filter((n) => !rejectedNodeIds.has(n.id))
      .map((n) => {
        if (
          n.type === "folder" &&
          n.children.some((cid) => rejectedNodeIds.has(cid))
        ) {
          return {
            ...n,
            children: n.children.filter((cid) => !rejectedNodeIds.has(cid)),
          };
        }

        return n;
      });

    const allRejectedFiles = [...rejected, ...replacementRejections];

    if (allRejectedFiles.length > 0) {
      setRejectedFiles((prev) => [...prev, ...allRejectedFiles]);
    }

    if (validNodes.length > 0 || mergeTargets.length > 0) {
      addNodes(validNodes, mergeTargets);
    }

    const newItems: UploadFileItem[] = [
      ...valid.map(
        (fe): UploadFileItem => ({
          nodeId: fe.nodeId,
          fileName: fe.resolvedName,
          file: fe.file,
          status: "pending",
        }),
      ),
      ...validReplacements.map(
        (replacement): UploadFileItem => ({
          nodeId: replacement.existingNodeId,
          fileName: replacement.resolvedName,
          file: replacement.file,
          status: "pending",
          isReplacement: true,
        }),
      ),
    ];

    if (newItems.length === 0) return;

    setItems((prev) => [...prev, ...newItems]);
    abortRef.current = false;
    await processFileQueue(newItems);
  }

  async function dispatchEntries(
    entries: DropEntry[],
    targetParentId: string | null,
  ) {
    if (entries.length === 0) return;

    const detected = buildTreeFromDropEntries(
      entries,
      targetParentId,
      getNodeMap(),
      "detect",
    );

    if (detected.conflicts.length === 0) {
      await enqueueFiles(
        detected.nodes,
        detected.fileEntries,
        detected.mergeTargets,
        detected.replacements,
      );

      return;
    }

    setDeferredDrop({ entries, targetParentId, detected });
  }

  async function handleDrop(
    items: DataTransferItem[] | DataTransferItemList,
    targetParentId: string | null,
  ) {
    if (deferredDrop) return;

    const entries = await readDropEntries(items);
    await dispatchEntries(entries, targetParentId);
  }

  async function handleFilePick(
    files: FileList,
    targetParentId: string | null,
  ) {
    if (deferredDrop) return;

    const dropEntries: DropEntry[] = [];

    for (let i = 0; i < files.length; i++) {
      dropEntries.push({ kind: "file", name: files[i].name, file: files[i] });
    }

    await dispatchEntries(dropEntries, targetParentId);
  }

  async function resolvePendingConflicts(
    strategy: ConflictResolution | "cancel",
  ) {
    const pending = deferredDrop;

    if (!pending) return;

    setDeferredDrop(null);

    if (strategy === "cancel") return;

    // For "replace", reuse the already-traversed tree from detect — build
    // replacements from the stored conflicts instead of re-walking entries.
    if (strategy === "replace") {
      const replacements: FileReplacement[] = pending.detected.conflicts.map(
        (c) => ({ existingNodeId: c.existingNodeId, file: c.droppedFile }),
      );
      await enqueueFiles(
        pending.detected.nodes,
        pending.detected.fileEntries,
        pending.detected.mergeTargets,
        replacements,
      );

      return;
    }

    // For "keep-both", re-run to produce deduplicated names for conflicting files.
    const result = buildTreeFromDropEntries(
      pending.entries,
      pending.targetParentId,
      getNodeMap(),
      strategy,
    );
    await enqueueFiles(
      result.nodes,
      result.fileEntries,
      result.mergeTargets,
      result.replacements,
    );
  }

  async function retryFailed() {
    const failedItems = items.filter((i) => i.status === "error");

    if (failedItems.length === 0) return;

    setItems((prev) =>
      prev.map((i) =>
        i.status === "error"
          ? { ...i, status: "pending", error: undefined }
          : i,
      ),
    );

    await processFileQueue(
      failedItems.map((i) => ({ ...i, status: "pending" as const })),
    );
  }

  async function retrySingle(nodeId: string) {
    const item = items.find((i) => i.nodeId === nodeId);

    if (!item || item.status !== "error") return;

    setItems((prev) =>
      prev.map((i) =>
        i.nodeId === nodeId ? { ...i, status: "pending", error: undefined } : i,
      ),
    );

    await processFileQueue([{ ...item, status: "pending" }]);
  }

  function removeItem(nodeId: string) {
    const item = items.find((i) => i.nodeId === nodeId);

    setItems((prev) => prev.filter((i) => i.nodeId !== nodeId));

    // Replacement items pre-existed — don't remove the underlying node.
    if (!item?.isReplacement) {
      removeNode(nodeId);
    }
  }

  function reset() {
    abortRef.current = true;
    setItems([]);
    setRejectedFiles([]);
    setDeferredDrop(null);
  }

  return {
    uploadState,
    isUploadActive: isUploading || hasErrors,
    pendingConflicts,
    resolvePendingConflicts,
    handleDrop,
    handleFilePick,
    retryFailed,
    retrySingle,
    removeItem,
    reset,
  };
}
